// public/v3/src/components/bhi-consent-pill.jsx — patient-level BHI consent.
// Mirrors the APCM consent pill: only rendered when the practice's
// combined-care-management setting is OFF (parent handles inheritance by
// rendering a separate "BHI consent ✓ inherited" badge instead of this pill).
// Click to open an inline form (date + verbal/written); save writes
// data.bhiConsent = { signed: true, date, source } to the patient blob.
// Once set, persists across months. Existing records can be edited or reset.
//
// When the practice's combined setting is ON, the parent renders a
// "BHI consent ✓ inherited" pill directly instead of this component.

import React, { useState, useEffect, useRef } from 'react';
import { ccmApi } from '../lib/api';
import { auditBhiConsentChange } from '../lib/consent-audit';

function normalizeConsent(consent) {
  if (consent === true) return { signed: true, date: '', source: 'verbal' };
  if (typeof consent === 'string') {
    const signed = consent.toLowerCase() === 'signed' || consent.toLowerCase() === 'true';
    return { signed, date: '', source: 'verbal' };
  }
  if (!consent || typeof consent !== 'object') return { signed: false, date: '', source: 'verbal' };
  return {
    signed: consent.signed === true || consent.signed === 'true' || consent.status === 'signed',
    date: consent.date || '',
    source: consent.source || 'verbal',
  };
}

export function BhiConsentPill({ patient, onSaved }) {
  const raw = patient?._raw || patient || {};
  const consent = raw.bhiConsent || null;
  const normalized = normalizeConsent(consent);
  const patientName = [raw.fname, raw.lname].filter(Boolean).join(' ') || patient?.name || 'this patient';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(normalized.date || new Date().toISOString().slice(0, 10));
  const [source, setSource] = useState(normalized.source || 'verbal');
  const [error, setError] = useState(null);
  const [flipUp, setFlipUp] = useState(false);
  const popRef = useRef(null);
  const triggerRef = useRef(null);

  // Reset form state when the popover opens or when the underlying patient
  // blob changes — useState retains stale local values across re-opens.
  // When there's no consent on file yet, default the date input to the
  // patient's first Elation office visit (matching CCM/BHI enrollment
  // defaults); the operator can still override. Falls back to today on
  // error / missing elation_id. A cancel flag guards against a stale
  // first-visit response clobbering a newer open, and we only seed the
  // fetched default if the operator hasn't edited the field during the
  // async gap (the input still holds the pre-fetch initial value).
  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    const initialDate = normalized.date || today;
    setDate(initialDate);
    setSource(normalized.source || 'verbal');
    setEditing(!normalized.signed);
    setError(null);
    if (!normalized.signed && !normalized.date && raw.elation_id) {
      let cancelled = false;
      (async () => {
        try {
          const res = await ccmApi.firstVisitDates([String(raw.elation_id)]);
          const fv = res.results && res.results[String(raw.elation_id)];
          if (cancelled || !fv) return;
          // Only apply the fetched default if the operator hasn't touched the
          // field yet — otherwise their edit would be clobbered on resolve.
          setDate((cur) => (cur === initialDate ? fv : cur));
        } catch (_) { /* keep today default */ }
      })();
      return () => { cancelled = true; };
    }
  }, [open, normalized.signed, normalized.date, normalized.source, raw.elation_id]);

  // Click-outside-to-close. popRef is on the outer span so clicks on the
  // trigger itself don't fire close-then-open.
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Escape-to-close.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);

  // Flip the popover upward when there's not enough room below.
  useEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // 300px is a generous estimate for popover height (form + buttons).
    setFlipUp(rect.bottom + 300 > window.innerHeight);
  }, [open]);

  // Preserves _updatedAt on the body so the server's 409 optimistic-lock
  // fires on stale writes; captures the response's new updatedAt into nextRaw
  // before onSaved so the parent's local cache stays current.
  async function save() {
    setBusy(true); setError(null);
    try {
      const prevConsent = raw.bhiConsent || null;
      const nextConsent = { signed: true, date, source };
      const next = { ...raw, bhiConsent: nextConsent };
      const resp = await ccmApi.updatePatient(patient.id, next);
      await auditBhiConsentChange(patient, prevConsent, nextConsent, { date, source });
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      onSaved?.(fresh ? { ...next, _updatedAt: fresh } : next);
      setOpen(false);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(e?.message || 'Save failed');
      }
    } finally { setBusy(false); }
  }

  async function reset() {
    const ok = confirm(`Reset BHI consent record for ${patientName}?`);
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      const prevConsent = raw.bhiConsent || null;
      const next = { ...raw };
      delete next.bhiConsent;
      const resp = await ccmApi.updatePatient(patient.id, next);
      await auditBhiConsentChange(patient, prevConsent, null);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      onSaved?.(fresh ? { ...next, _updatedAt: fresh } : next);
      setOpen(false);
      setEditing(false);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(e?.message || 'Reset failed');
      }
    } finally { setBusy(false); }
  }

  function renderForm() {
    return (
      <>
        <div className="bhi-pill-pop-title">Date + source</div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={busy}
          className="bhi-pill-input"
        />
        <div className="bhi-pill-radio-row">
          <label>
            <input type="radio" name={`bhi-src-${patient.id}`} value="verbal" checked={source === 'verbal'} onChange={() => setSource('verbal')} disabled={busy} />
            Verbal
          </label>
          <label>
            <input type="radio" name={`bhi-src-${patient.id}`} value="written" checked={source === 'written'} onChange={() => setSource('written')} disabled={busy} />
            Written
          </label>
        </div>
        <div className="bhi-pill-actions">
          <button className="btn ghost sm" onClick={() => normalized.signed ? setEditing(false) : setOpen(false)} disabled={busy}>Cancel</button>
          <button className="btn primary sm" onClick={save} disabled={busy || !date}>Save</button>
        </div>
        {error && <div className="bhi-pill-error">{error}</div>}
      </>
    );
  }

  if (normalized.signed) {
    return (
      <span ref={popRef} className="bhi-pill ok" title={`BHI consent ${normalized.date || 'date not set'} (${normalized.source})`}>
        <button
          ref={triggerRef}
          className="bhi-pill-btn"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label="BHI consent on file — open details"
          onClick={() => setOpen(o => !o)}>
          BHI consent ✓ <span className="tabnum">{normalized.date || 'date not set'}</span>
        </button>
        {open && (
          <div className={`bhi-pill-pop ${flipUp ? 'up' : ''}`} onClick={(e) => e.stopPropagation()}>
            {editing ? renderForm() : (
              <>
                <div className="bhi-pill-pop-title">BHI consent on file</div>
                <div className="bhi-pill-detail"><span>Date</span><b className="tabnum">{normalized.date || 'Not set'}</b></div>
                <div className="bhi-pill-detail"><span>Source</span><b>{normalized.source}</b></div>
                <div className="bhi-pill-actions split">
                  <button className="btn danger sm" onClick={reset} disabled={busy}>Reset</button>
                  <span className="spacer" />
                  <button className="btn sm" onClick={() => setEditing(true)} disabled={busy}>Edit</button>
                  <button className="btn primary sm" onClick={() => setOpen(false)} disabled={busy}>Done</button>
                </div>
                {error && <div className="bhi-pill-error">{error}</div>}
              </>
            )}
          </div>
        )}
      </span>
    );
  }

  return (
    <span ref={popRef} className="bhi-pill">
      <button
        ref={triggerRef}
        className="bhi-pill-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Document BHI consent"
        onClick={() => setOpen(o => !o)}>
        BHI consent ▢
      </button>
      {open && (
        <div className={`bhi-pill-pop ${flipUp ? 'up' : ''}`} onClick={(e) => e.stopPropagation()}>
          {renderForm()}
        </div>
      )}
    </span>
  );
}
